import { computed, type ComputedRef } from "vue"

import {
  useApplicationStore,
  useFieldStore,
  useProviderStore,
} from "@versatile-credit-apply/sdk"
import { useRoute } from "vue-router"

type StandardStepId = "applicant" | "reviewOffer" | "terms" | "decision"
type CustomStepId = "link-sent"

export type StepId = StandardStepId | CustomStepId

export interface StepperDefinition {
  id: string
  steps: Array<StepId>
}

const DefaultStepDefinition: StepperDefinition = {
  id: "default",
  steps: ["applicant", "terms", "decision"],
}

const ReviewOfferStepDefinition: StepperDefinition = {
  id: "review-and-accept-offer",
  steps: ["reviewOffer", "terms", "decision"],
}

const HostedLinkStepDefinition: StepperDefinition = {
  id: "hosted-link",
  steps: ["applicant", "link-sent"],
}

const AllSteppers = [
  DefaultStepDefinition,
  ReviewOfferStepDefinition,
  HostedLinkStepDefinition,
]

const useStepperByApplicationState = (): ComputedRef<
  StepperDefinition | undefined
> => {
  const applicationStore = useApplicationStore()
  const fieldStore = useFieldStore()
  const providerStore = useProviderStore()

  return computed(() => {
    const providerKey = providerStore.currentProviderKey

    if (!providerKey) {
      return DefaultStepDefinition
    }

    const transition = applicationStore.previousResponses.find(
      (response) => response.next?.provider?.providerKey === providerKey
    )
    if (
      transition?.next?.applyMode === "prescreen" &&
      fieldStore.fieldCount === 0
    ) {
      return ReviewOfferStepDefinition
    }

    return undefined
  })
}

const useStepperById = (): ComputedRef<StepperDefinition | undefined> => {
  const route = useRoute()

  return computed(() => {
    const requestedId = route?.meta?.stepper?.id
    return requestedId
      ? AllSteppers.find((stepper) => stepper.id === requestedId)
      : undefined
  })
}

export const useStepperDefinition = (): ComputedRef<StepperDefinition> => {
  const stepperByApplicationState = useStepperByApplicationState()
  const stepperById = useStepperById()

  return computed(
    () =>
      stepperById.value ||
      stepperByApplicationState.value ||
      DefaultStepDefinition
  )
}
